// ============================================================================
// The order's activity log — everything that has happened to this order, in
// order, in English.
//
// The rows arrive already rendered (see `oms/sales_order/services/event_log.py`):
// the backend owns the event vocabulary, so this file is presentation only. What
// it adds is the READING of them —
//
//   * system bookkeeping is hidden by default. Roughly half of the raw log is a
//     leg being planned or a notification document being queued; shown flat, the
//     handful of rows an operator is actually looking for drown in it. The
//     toggle brings them back, so the log stays complete for debugging.
//   * the technical detail (for a failed write: the exact body sent to Zoho and
//     the reply) sits behind a per-row expand, not on the surface.
//
// Read-only. Every mutation on this order has its own control on the page.
// ============================================================================
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone,
  InfoCircleTwoTone,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Segmented,
  Space,
  Spin,
  Switch,
  Tag,
  Timeline,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fmtIST } from "../format";
import { getSalesOrderLog } from "../oms.api";
import { LogCategory, LogSeverity, OrderLogRow } from "../types";

// antd's Timeline colours the rail dot; the icon carries the same meaning for anyone who can't
// rely on colour alone.
const SEVERITY_ICON: Record<LogSeverity, React.ReactNode> = {
  info: <InfoCircleTwoTone twoToneColor="#8c8c8c" />,
  success: <CheckCircleTwoTone twoToneColor="#52c41a" />,
  warning: <ExclamationCircleTwoTone twoToneColor="#faad14" />,
  error: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
};

const CATEGORY_LABEL: Record<LogCategory, string> = {
  order: "Order",
  sourcing: "Sourcing",
  purchase: "Purchase",
  warehouse: "Warehouse",
  logistics: "Delivery",
  documents: "Zoho",
  escalation: "Escalation",
};

const CATEGORY_COLOR: Record<LogCategory, string> = {
  order: "default",
  sourcing: "geekblue",
  purchase: "purple",
  warehouse: "cyan",
  logistics: "blue",
  documents: "gold",
  escalation: "volcano",
};

// The quick cuts.
//
// "People" leads and is the DEFAULT: the question this drawer is opened to answer is almost always
// "who did this, and why" — and the overwhelming majority of the raw log is the system talking to
// itself. Opening on everything buries the handful of rows with a person's name on them.
// "Problems" earns its place for the other case: when an order goes red the operator wants the
// failures and nothing else, and scrolling 300 rows to find three of them is why people gave up on
// the log and opened the database instead.
type Cut = "people" | "problems" | "all";

const CUTS: { label: string; value: Cut }[] = [
  { label: "People", value: "people" },
  { label: "Problems", value: "problems" },
  { label: "Everything", value: "all" },
];

interface Props {
  salesOrderId?: number;
  open: boolean;
  onClose: () => void;
}

const LogsDrawer: React.FC<Props> = ({ salesOrderId, open, onClose }) => {
  const [rows, setRows] = useState<OrderLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showSystem, setShowSystem] = useState(false);
  const [cut, setCut] = useState<Cut>("people");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!salesOrderId) return;
    setLoading(true);
    setFailed(false);
    const data = await getSalesOrderLog(salesOrderId);
    // `undefined` is the api module's failure signal (it has already surfaced the toast). Distinguish
    // it from a genuinely empty log, or a broken fetch reads as "nothing ever happened".
    if (data) setRows(data.results);
    else setFailed(true);
    setLoading(false);
  }, [salesOrderId]);

  // Fetched on OPEN, not on mount — the log is a few hundred rows and most orders are never asked
  // for theirs. Re-fetched on each open so a drawer reopened after an action is not stale.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showSystem && r.is_noise) return false;
      if (cut === "problems" && r.severity !== "error" && r.severity !== "warning") return false;
      if (cut === "people" && !r.actor) return false;
      if (!needle) return true;
      return [r.title, r.detail, r.context, r.actor]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(needle));
    });
  }, [rows, showSystem, cut, search]);

  const hiddenCount = rows.length - visible.length;

  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Drawer
      title="Order log"
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Space wrap size={12} style={{ width: "100%", justifyContent: "space-between" }}>
          <Segmented
            size="small"
            options={CUTS}
            value={cut}
            onChange={(v) => setCut(v as Cut)}
          />
          <Space size={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              System detail
            </Typography.Text>
            <Switch size="small" checked={showSystem} onChange={setShowSystem} />
          </Space>
        </Space>

        <Input.Search
          allowClear
          size="small"
          placeholder="Search this log"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {failed && (
          <Alert
            type="error"
            showIcon
            message="Could not load the log"
            description="Nothing here means the log could not be fetched — not that the order has no history."
          />
        )}

        {loading ? (
          <Spin style={{ display: "block", marginTop: 40 }} />
        ) : visible.length === 0 ? (
          // The "People" default can legitimately come up empty — an order that has only ever been
          // moved by the pipeline has no named rows at all. Say that, rather than showing a bare
          // "nothing found" that reads as though the log failed to load, and offer the way out.
          <Empty
            description={
              cut === "people" && !search
                ? "Nobody has acted on this order by hand — everything so far was done automatically."
                : hiddenCount > 0
                ? `Nothing matches — ${hiddenCount} row${hiddenCount === 1 ? "" : "s"} hidden by the filters`
                : "No activity recorded for this order"
            }
          >
            {cut !== "all" && (
              <Button size="small" onClick={() => setCut("all")}>
                Show everything
              </Button>
            )}
          </Empty>
        ) : (
          <>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {visible.length} of {rows.length} entries
              {hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ""}
            </Typography.Text>
            <Timeline
              items={visible.map((r) => ({
                dot: SEVERITY_ICON[r.severity],
                children: (
                  <LogEntry
                    row={r}
                    expanded={expanded.has(r.id)}
                    onToggle={() => toggleRow(r.id)}
                  />
                ),
              }))}
            />
          </>
        )}
      </Space>
    </Drawer>
  );
};

const LogEntry: React.FC<{
  row: OrderLogRow;
  expanded: boolean;
  onToggle: () => void;
}> = ({ row, expanded, onToggle }) => {
  // A failed row's Zoho bodies are the whole reason the technical panel exists — call that out on
  // the button so nobody has to expand rows at random to find where the diagnosis is.
  const hasZohoBodies =
    row.raw?.zoho_request !== undefined || row.raw?.zoho_response !== undefined;

  return (
    <div style={{ paddingBottom: 4 }}>
      <Space wrap size={6} align="center">
        <Typography.Text strong>{row.title}</Typography.Text>
        {/* WHO did it, right beside what happened. This is the question the drawer is opened to
            answer, so the name gets a tag of its own rather than sitting in the grey metadata line
            where it read as filing detail. */}
        {row.actor && (
          <Tag icon={<UserOutlined />} color="blue" style={{ marginInlineEnd: 0 }}>
            {row.actor}
          </Tag>
        )}
        <Tag color={CATEGORY_COLOR[row.category] ?? "default"} style={{ marginInlineEnd: 0 }}>
          {CATEGORY_LABEL[row.category] ?? row.category}
        </Tag>
        {row.is_noise && (
          <Tag style={{ marginInlineEnd: 0 }} color="default">
            system
          </Tag>
        )}
      </Space>

      {row.detail && (
        <div>
          <Typography.Text style={{ fontSize: 13 }}>{row.detail}</Typography.Text>
        </div>
      )}

      <div style={{ marginTop: 2 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {fmtIST(row.ts, "DD MMM YYYY HH:mm")} IST
          {/* A named row shows its person in the tag above, so it is not repeated here. An unnamed
              one still says "System" — plainer than an empty space that reads like missing data. */}
          {row.actor ? "" : " · System"}
          {row.context ? ` · ${row.context}` : ""}
        </Typography.Text>
      </div>

      {row.raw && (
        <div style={{ marginTop: 4 }}>
          <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={onToggle}>
            {expanded
              ? "Hide technical detail"
              : hasZohoBodies
              ? "Show what we sent Zoho and what came back"
              : "Show technical detail"}
          </Button>
          {expanded && (
            <pre
              style={{
                margin: "6px 0 0",
                padding: 8,
                fontSize: 11,
                lineHeight: 1.5,
                maxHeight: 320,
                overflow: "auto",
                background: "#fafafa",
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {safeJson(row.raw)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// The raw block is provider-derived (Zoho / carrier envelopes), so it can hold anything —
// including a cycle. React throws on an object child and, unguarded, that unmounts the page; the
// same hazard `fmtText` exists for, one level deeper.
const safeJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export default LogsDrawer;
